import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Loader2,
  Trash2,
  X,
} from 'lucide-react'

import { type ReactNode, useEffect, useState } from 'react'
import { ActionCell } from '@/components/ui/action-cell'
import { Checkbox } from '@/components/ui/checkbox'
import { Pagination } from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { DataTableBulkAction } from './data-table.types'

export type { DataTableBulkAction } from './data-table.types'

export type SortDirection = 'asc' | 'desc' | null

export interface SortState {
  column: string | null
  direction: SortDirection
}

export interface DataTableColumn<T = unknown> {
  key: string
  label: string
  render?: (item: T) => ReactNode
  className?: string
  headerClassName?: string
  sortable?: boolean
}

export interface DataTableAction<T = unknown> {
  label: string
  icon?: ReactNode
  onClick: (item: T) => void
  variant?: 'default' | 'destructive'
}

export interface DataTablePagination {
  currentPage: number
  totalPages: number
  totalCount: number
  perPage: number
}

export interface DataTableProps<T = unknown> {
  data: T[]
  columns: DataTableColumn<T>[]
  loading?: boolean
  // Selection
  selectable?: boolean
  onItemSelect?: (itemId: string, selected: boolean) => void
  selectedItems?: Set<string>
  getItemId?: (item: T) => string
  onBulkDelete?: (selectedIds: string[]) => void
  bulkActions?: DataTableBulkAction[]
  // Actions
  actions?: DataTableAction<T>[]
  getActions?: (item: T) => DataTableAction<T>[]
  onRowClick?: (item: T) => void
  // Pagination
  pagination?: DataTablePagination
  onPageChange?: (page: number) => void
  onPerPageChange?: (perPage: number) => void
  // Sorting
  sortState?: SortState
  onSort?: (column: string, direction: SortDirection) => void
  // Styling
  containerClassName?: string
  emptyMessage?: string
  // Sticky headers
  stickyHeader?: boolean
  maxHeight?: string
  // Header visibility
  showHeader?: boolean
}

export function DataTable<T = unknown>({
  data,
  columns,
  loading = false,
  selectable = false,
  onItemSelect,
  selectedItems = new Set(),
  getItemId,
  onBulkDelete,
  bulkActions = [],
  actions = [],
  getActions,
  onRowClick,
  pagination,
  onPageChange,
  onPerPageChange,
  sortState,
  onSort,
  containerClassName = '',
  emptyMessage = 'No data found.',
  stickyHeader = false,
  maxHeight = '400px',
  showHeader = true,
}: DataTableProps<T>) {
  const [selectAll, setSelectAll] = useState(false)

  // Update selectAll state when selectedItems changes
  useEffect(() => {
    if (data.length === 0) {
      setSelectAll(false)
      return
    }

    const allSelected = data.every((item) => {
      if (!getItemId) return false
      const itemId = getItemId(item)
      return selectedItems.has(itemId)
    })

    setSelectAll(allSelected)
  }, [selectedItems, data, getItemId])

  const handleSelectAll = (checked: boolean) => {
    if (!selectable || !onItemSelect || !getItemId) return

    data.forEach((item) => {
      const itemId = getItemId(item)
      onItemSelect(itemId, checked)
    })
  }

  const handleHeaderCheckboxChange = (checked: boolean) => {
    handleSelectAll(checked)
  }

  const handleSort = (columnKey: string) => {
    if (!onSort) return

    let newDirection: SortDirection = 'asc'

    if (sortState?.column === columnKey) {
      if (sortState.direction === 'asc') {
        newDirection = 'desc'
      } else if (sortState.direction === 'desc') {
        newDirection = null
      } else {
        newDirection = 'asc'
      }
    }

    onSort(columnKey, newDirection)
  }

  const getSortIcon = (columnKey: string) => {
    if (!sortState || sortState.column !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
    }

    if (sortState.direction === 'asc') {
      return <ArrowUp className="h-3 w-3 text-primary" />
    } else if (sortState.direction === 'desc') {
      return <ArrowDown className="h-3 w-3 text-primary" />
    }

    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
  }

  const handleBulkDelete = () => {
    if (!onBulkDelete || selectedItems.size === 0) return
    const selectedIds = Array.from(selectedItems)
    onBulkDelete(selectedIds)
  }

  const handleClearSelection = () => {
    if (!selectable || !onItemSelect || !getItemId) return

    data.forEach((item) => {
      const itemId = getItemId(item)
      if (selectedItems.has(itemId)) {
        onItemSelect(itemId, false)
      }
    })
  }

  // Returns row specific actions (e.g., Edit, Delete) that appear in a dropdown menu
  // for each table row. Uses dynamic getActions() if provided otherwise falls back to static actions array.
  const getItemActions = (item: T): DataTableAction<T>[] => {
    return getActions ? getActions(item) : actions
  }

  const hasActions = (): boolean => {
    if (getActions) {
      return data.some((item) => getItemActions(item).length > 0)
    }
    return actions.length > 0
  }

  const renderCell = (item: T, column: DataTableColumn<T>) => {
    if (column.render) {
      return column.render(item)
    }

    // Default rendering: try to access the property by key
    const value = (item as Record<string, unknown>)[column.key]
    return value !== undefined && value !== null ? String(value) : '-'
  }

  if (!loading && data.length === 0) {
    return (
      <div
        className={`bg-background rounded-2xl border border-border shadow-sm ${containerClassName}`}
      >
        <div className="p-8 text-center text-muted-foreground">
          {emptyMessage}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`bg-background rounded-2xl border border-border shadow-sm w-full overflow-auto ${containerClassName}`}
    >
      <div
        className={
          stickyHeader ? 'relative overflow-y-auto' : 'overflow-x-auto'
        }
        style={stickyHeader ? { maxHeight } : undefined}
      >
        <Table noWrapper={stickyHeader}>
          {showHeader && (
            <TableHeader
              className={
                stickyHeader
                  ? 'sticky top-0 z-10 bg-secondary/40 backdrop-blur-[10px]'
                  : 'bg-secondary/40 backdrop-blur-[10px]'
              }
            >
              <TableRow className="bg-secondary/40 border-b border-border hover:bg-secondary/40 backdrop-blur-[10px]">
                {selectable && (
                  <TableHead className="w-12 !px-6 !py-3 !pr-6">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={(checked) => {
                          handleHeaderCheckboxChange(checked as boolean)
                        }}
                        disabled={loading}
                        className="h-4 w-4"
                      />
                    </div>
                  </TableHead>
                )}

                {/* Bulk Selection Header - Replace column headers when items are selected */}
                {selectable && selectedItems.size > 0 ? (
                  <TableHead
                    colSpan={columns.length + (hasActions() ? 1 : 0)}
                    className="h-12 !px-6 !py-3"
                  >
                    <div className="flex items-center gap-6 h-full">
                      <span className="text-sm font-medium text-primary">
                        {selectedItems.size} selected
                      </span>
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Clear selection
                      </button>
                      {bulkActions.map((action) => (
                        <button
                          key={action.label}
                          type="button"
                          onClick={() =>
                            action.onClick(Array.from(selectedItems))
                          }
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {action.label}
                        </button>
                      ))}
                      {onBulkDelete && (
                        <button
                          type="button"
                          onClick={handleBulkDelete}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete {selectedItems.size} item
                          {selectedItems.size === 1 ? '' : 's'}
                        </button>
                      )}
                    </div>
                  </TableHead>
                ) : (
                  /* Normal column headers when no items selected */
                  <>
                    {columns.map((column, index) => (
                      <TableHead
                        key={column.key}
                        className={`${index >= 2 ? 'min-w-[250px]' : ''} !px-6 !py-3 text-sm font-medium text-foreground ${column.headerClassName || ''}`}
                      >
                        {column.sortable && onSort ? (
                          <div className="flex items-center justify-between">
                            <span>{column.label}</span>
                            <button
                              type="button"
                              onClick={() => handleSort(column.key)}
                              className="ml-2 p-1 hover:bg-muted rounded transition-colors"
                              aria-label={`Sort by ${column.label}`}
                            >
                              {getSortIcon(column.key)}
                            </button>
                          </div>
                        ) : (
                          column.label
                        )}
                      </TableHead>
                    ))}
                    {hasActions() && (
                      <TableHead className="w-12 !px-2 !py-3">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    )}
                  </>
                )}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            {loading ? (
              // Loading state - show centered spinner
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (hasActions() ? 1 : 0)
                  }
                  className="px-6 py-8 text-center"
                >
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              // Empty state - show message in center
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (hasActions() ? 1 : 0)
                  }
                  className="px-6 py-8 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              // Data rows
              data.map((item, index) => {
                const itemId = getItemId ? getItemId(item) : String(index)

                const handleRowCheckboxChange = (checked: boolean) => {
                  if (onItemSelect) {
                    onItemSelect(itemId, checked)
                  }
                }

                return (
                  <TableRow
                    key={itemId}
                    data-item-id={itemId}
                    className={`border-b border-border hover:bg-secondary ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick?.(item)}
                  >
                    {selectable && (
                      <TableCell
                        className="!px-6 !py-3 !pr-6"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedItems.has(itemId)}
                            onCheckedChange={handleRowCheckboxChange}
                            className="h-4 w-4"
                          />
                        </div>
                      </TableCell>
                    )}
                    {columns.map((column, index) => (
                      <TableCell
                        key={column.key}
                        className={`${index >= 2 ? 'min-w-[250px]' : ''} !px-6 !py-3 ${column.className || ''}`}
                      >
                        {renderCell(item, column)}
                      </TableCell>
                    ))}
                    <ActionCell item={item} actions={getItemActions(item)} />
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Bottom Pagination */}
      {pagination && onPageChange && !loading && (
        <div className="px-6 py-4 border-t border-border bg-secondary/40">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            perPage={pagination.perPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}
    </div>
  )
}
